import {
	DatePickerProvider,
	DatePickerControl,
	DatePickerDayView,
	DatePickerMonthView,
	DatePickerPickerRoot,
	DatePickerRoot,
	DatePickerTrigger,
	DatePickerYearView,
} from '~/components/date_based_pickers/components'
import {date, formatMultipleDates} from '@eitje/utils'
import {EitjeButton} from '~/components/eitje_button'
import {Text} from '~/components/text'
import {Layout} from '~/components/layout'

/**
 * @typedef {Object} MultipleDatePickerProps
 * @property {(string | import('moment').Moment)[] | null} [value]
 * Controlled selected dates. Strings are normalized to the date values expected by Ark UI; null or an empty array clears the picker.
 * @property {(value: import('moment').Moment[]) => void} onChange Called with every selected date converted to a Moment.
 * @property {string | import('moment').Moment} [defaultPickerValue] Initial date focused when the picker opens.
 * @property {(datePicker: import('~/components/date_based_pickers/helpers').MultipleDatePickerContextValues) => import('react').ReactNode} [renderTrigger]
 * Renders a custom non-editable trigger with the adapted date-picker context. The default trigger displays the formatted selected dates.
 * @property {string | import('moment').Moment} [minDate] Earliest selectable date, inclusively.
 * @property {string | import('moment').Moment} [maxDate] Latest selectable date, inclusively.
 * @property {string} [placeholder] Placeholder shown by the default trigger when no dates are selected.
 * @property {boolean} [disabled] Prevents interaction with the picker.
 * @property {boolean} [readOnly] Prevents value changes while retaining the selected dates.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {(date: import('moment').Moment) => boolean} [disabledDateFn] Predicate for whether a date should be disabled.
 */

/**
 * @param {MultipleDatePickerProps} props
 */
export const MultipleDatePicker = ({
	value,
	defaultPickerValue,
	placeholder,
	minDate,
	maxDate,
	disabled,
	readOnly,
	renderTrigger,
	onOpenChange,
	onChange,
	disabledDateFn,
}) => {
	let triggerElement = (
		<DatePickerProvider>
			{ctx => (
				<DatePickerTrigger>
					<EitjeButton dropdown>
						<Text>{ctx.value.length === 0 && placeholder ? placeholder : formatMultipleDates(ctx.value)}</Text>
					</EitjeButton>
				</DatePickerTrigger>
			)}
		</DatePickerProvider>
	)
	if (renderTrigger)
		triggerElement = <DatePickerProvider>{ctx => <DatePickerTrigger>{renderTrigger(ctx)}</DatePickerTrigger>}</DatePickerProvider>

	const hasValue = value?.length > 0
	// Convert ambigous props from (String | Moment) to Moment
	const minDateOverride = minDate ? date(minDate) : undefined
	const maxDateOverride = maxDate ? date(maxDate) : undefined
	const defaultFocusedValueOverride = defaultPickerValue ? date(defaultPickerValue) : undefined
	const valueOverride = hasValue ? value.map(selectedDate => date(selectedDate)) : []

	return (
		<DatePickerRoot
			value={valueOverride}
			defaultFocusedValue={defaultFocusedValueOverride}
			onChange={onChange}
			mode="multiple"
			minDate={minDateOverride}
			maxDate={maxDateOverride}
			disabled={disabled}
			readOnly={readOnly}
			onOpenChange={onOpenChange}
			disabledDateFn={disabledDateFn}
		>
			<DatePickerControl>{triggerElement}</DatePickerControl>
			<DatePickerPickerRoot
				renderExtraFooter={ctx => (
					<Layout horizontal="spaceBetween">
						<Layout onClick={ctx.clear} baseColor="red">
							<Text t="common.reset" color={hasValue ? 'red' : 'black'} />
						</Layout>
						<EitjeButton
							t="common.keep"
							height="small"
							onClick={() => ctx.setOpen(false)}
							colorSet={hasValue ? 'solid' : 'grey-bordered'}
						/>
					</Layout>
				)}
			>
				<DatePickerDayView />
				<DatePickerMonthView />
				<DatePickerYearView />
			</DatePickerPickerRoot>
		</DatePickerRoot>
	)
}
